<script setup lang="ts">
import { onMounted, ref } from "vue";
import { focusFirstElementInDialog, isMobileView, removeQueryParameter } from "../../utils/GeneralUtils.ts";
import { useRoute, useRouter } from "vue-router";
import { useUserManual } from "../../composables/useUserManual.ts";
import ManualImageZoom from "./ManualImageZoom.vue";
import ManualSearchHeader from "./ManualSearchHeader.vue";

const props = defineProps({
    visible: {
        type: Boolean,
        required: true,
    },
});

const route = useRoute();
const router = useRouter();

const emit = defineEmits(["update:visible"]);

const {
    searchTerm,
    filteredContent,
    matchCount,
    setContentContainer,
    jumpToNextMatch,
    clearSearchTerm,
    loadContent,
    zoomedImage,
    openImageFromEvent,
    closeImage,
    t,
} = useUserManual();

const manualOpen = ref(props.visible);

onMounted(async () => {
    await loadContent();
    if (searchTerm.value && searchTerm.value !== "") {
        manualOpen.value = true;
    }
    focusFirstElementInDialog(`user-manual-dialog_dialog`);
});

function closeManual() {
    manualOpen.value = false;
    removeQueryParameter(route, router, "search");
    emit("update:visible", false);
}
</script>

<template>
    <Dialog
        id="user-manual-dialog_dialog"
        :visible="manualOpen"
        modal
        :style="{ width: '80vw' }"
        :dismissable-mask="true"
        :block-scroll="true"
        :draggable="false"
        :closable="false"
        :pt="{
            // Reserve the same scrollbar gutter in header and content: with classic (non-overlay)
            // scrollbars the content would otherwise be narrower than the header, and the search
            // would no longer end at the right edge of the manual's panels.
            header: { style: { overflow: 'hidden', scrollbarGutter: 'stable' } },
            content: { style: { scrollbarGutter: 'stable' } },
        }"
    >
        <template #header>
            <ManualSearchHeader
                v-model:search-term="searchTerm"
                id-prefix="user-manual-dialog"
                :heading="t('profile-overview.instructions.heading')"
                :placeholder="t('fields.search')"
                :match-text="t('fields.searchMatches', { count: matchCount }, matchCount)"
                @next="jumpToNextMatch"
                @clear="clearSearchTerm"
            >
                <template #trailing>
                    <Button
                        id="user-manual-dialog_btn_header_close"
                        severity="secondary"
                        v-if="!isMobileView()"
                        text
                        icon="pi pi-times"
                        @click="closeManual"
                    />
                </template>
            </ManualSearchHeader>
        </template>
        <div :ref="setContentContainer" v-html="filteredContent" @click="openImageFromEvent"></div>
        <ManualImageZoom :image="zoomedImage" @close="closeImage" />
        <ScrollTop
            id="user-manual-dialog_scroll-top"
            target="parent"
            :threshold="100"
            icon="pi pi-arrow-up"
            :pt="{
                root: 'w-2rem h-2rem bg-primary text-white',
                icon: {
                    class: 'text-base w-1rem h-1rem',
                },
            }"
        />
        <template #footer>
            <Button
                id="user-manual-dialog_btn_footer_close"
                severity="secondary"
                :label="t('buttons.close')"
                icon="pi pi-times"
                @click="closeManual"
                text
            />
        </template>
    </Dialog>
</template>

<style scoped>
.desktop-only {
    display: none;
}

@media (min-width: 768px) {
    .desktop-only {
        display: inline;
    }
}
</style>
